import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { CustomerServiceService } from '../services/customer-service.service';
import { SnackBarService } from '../services/snack-bar.service';
import { ConfirmationDialogComponent } from './confirmation-dialog.component';
import { environment } from '../../environments/environment';

interface DocumentItem {
  id: string;
  name: string;
  required: boolean;
  checked: boolean;
  type: 'checkbox' | 'upload' | 'input';
  uploadedFiles?: any[];
  inputValue?: string;
  selected?: boolean; // For approval selection
  verified?: boolean; // Whether document is verified
}

interface ActivityLogItem {
  id: string;
  action: string;
  timestamp: Date;
  user: string;
  details: string;
}

@Component({
  selector: 'app-verification-view',
  templateUrl: './verification-view.component.html',
  styleUrls: ['./verification-view.component.css']
})
export class VerificationViewComponent implements OnInit {
  customerId: string = '';
  customerData: any = null;
  verificationData: any = null;
  documents: DocumentItem[] = [];
  activityLog: ActivityLogItem[] = [];
  additionalNotes: string = '';
  isLoading = false;
  isLoadingCustomer = false;
  isLoadingVerification = false;
  isLoadingActivityLog = false;
  isApprovingDocuments = false;
  isSendingVerificationLink = false;
  selectAll = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerServiceService,
    private snackBarService: SnackBarService,
    private dialog: MatDialog,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    this.customerId = this.route.snapshot.paramMap.get('id') || '';
    if (this.customerId) {
      this.loadCustomerData();
      this.loadVerificationData();
      this.loadActivityLog();
    }
  }

  loadCustomerData(): void {
    this.isLoadingCustomer = true;
    // Load customer details
    this.customerService.getCustomerById(this.customerId).subscribe({
      next: (response) => {
        // Try different response formats
        if (response && response.data) {
          this.customerData = response.data;
        } else if (response) {
          this.customerData = response;
        }
        this.isLoadingCustomer = false;
      },
      error: (error) => {
        console.error('Error loading customer data:', error);
        this.snackBarService.openSnackBar('Error loading customer data', 'error-class');
        // Don't set to null, let verification data populate it
        this.isLoadingCustomer = false;
      }
    });
  }

  loadVerificationData(): void {
    // Fetch verification data from API
    this.isLoadingVerification = true;
    const apiUrl = `${environment.BASE_URL}/view-form-admin/${this.customerId}`;
    
    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {        
        
        if (response && response.data) {
          const data = response.data;
          
          // Store customer info with all details - try multiple paths
          if (data.customer) {
            this.customerData = data.customer;
          } else if (data.customer_name || data.customer_email || data.customer_phone) {
            // If customer info is at root level of data
            this.customerData = data;
          }
          
          // Extract notes from API response - try multiple paths
          if (data.textarea && data.textarea.notes && data.textarea.notes.field_value) {
            this.additionalNotes = data.textarea.notes.field_value;
          } else if (data.notes) {
            // If notes is an object with field_value property
            if (typeof data.notes === 'object' && data.notes.field_value) {
              this.additionalNotes = data.notes.field_value;
            } else if (typeof data.notes === 'string') {
              this.additionalNotes = data.notes;
            }
          } else if (data.additional_notes) {
            // Try alternative key
            if (typeof data.additional_notes === 'object' && data.additional_notes.field_value) {
              this.additionalNotes = data.additional_notes.field_value;
            } else if (typeof data.additional_notes === 'string') {
              this.additionalNotes = data.additional_notes;
            }
          }
                    
          // Map documents from API response
          if (data.fields && Array.isArray(data.fields)) {
            this.documents = data.fields.map((field: any) => ({
              id: field.id || this.generateFieldId(field.name),
              name: field.name || '',
              required: field.required === 1 || field.required === true,
              checked: field.verified === true || field.verified === 1 || (field.uploadedFiles && field.uploadedFiles.length > 0),
              type: field.type || 'upload',
              allows_multiple: field.allows_multiple === true || field.allows_multiple === 1,
              uploadedFiles: field.uploadedFiles && Array.isArray(field.uploadedFiles) ? field.uploadedFiles.map((file: any) => ({
                file: null,
                id: file.name || this.generateFileId(),
                name: file.name || '',
                url: file.url || file.file_url || '' // Include file URL
              })) : [],
              inputValue: field.inputValue || field.value || '',
              selected: false,
              verified: field.verified === true || field.verified === 1
            }));
          }
          
          this.verificationData = {
            isVerified: true,
            documentsSubmitted: true,
            email: data.customer?.customer_email || data.customer_email || '',
            verifiedAt: new Date().getTime(),
            documents: this.documents
          };
        } else if (response) {
          // If response is directly the data
          if (response.customer_name || response.customer_email || response.customer_phone) {
            this.customerData = response;
          }
          // Check for notes at root level
          if (response.notes) {
            if (typeof response.notes === 'object' && response.notes.field_value) {
              this.additionalNotes = response.notes.field_value;
            } else if (typeof response.notes === 'string') {
              this.additionalNotes = response.notes;
            }
          }
        }
        
        this.isLoadingVerification = false;
        this.updateSelectAllState();
      },
      error: (error) => {
        console.error('Error loading verification data:', error);
        this.isLoadingVerification = false;
        // Use sample data if API fails
        this.loadSampleDocuments();
        this.updateSelectAllState();
      }
    });
  }

  loadSampleDocuments(): void {
    // No sample data - only use API data
    this.documents = [];
  }

  loadActivityLog(): void {
    // Fetch activity log from API
    this.isLoadingActivityLog = true;
    const apiUrl = `${environment.BASE_URL}/activity-logs/${this.customerId}`;
    
    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {        
        // Map API response to ActivityLogItem format
        if (response && Array.isArray(response)) {
          this.activityLog = response.map((log: any) => ({
            id: log.id?.toString() || Date.now().toString(),
            action: log.action || 'Unknown Action',
            timestamp: new Date(log.created_at || new Date()),
            user: this.getActorName(log.actor),
            details: `${log.action} from ${log.ip_address || 'Unknown IP'}`
          }));
        } else if (response && response.data && Array.isArray(response.data)) {
          this.activityLog = response.data.map((log: any) => ({
            id: log.id?.toString() || Date.now().toString(),
            action: log.action || 'Unknown Action',
            timestamp: new Date(log.created_at || new Date()),
            user: this.getActorName(log.actor),
            details: `${log.action}`
          }));
        }
        
        // Sort by timestamp descending (newest first)
        this.activityLog.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        this.isLoadingActivityLog = false;
      },
      error: (error) => {
        console.error('Error loading activity log:', error);
        this.activityLog = [];
        this.isLoadingActivityLog = false;
      }
    });
  }

  getActorName(actor: string): string {
    const actorMap: { [key: string]: string } = {
      'customer': 'Customer',
      'admin': 'Admin',
      'system': 'System',
      'staff': 'Staff'
    };
    return actorMap[actor?.toLowerCase()] || actor || 'Unknown';
  }

  sendVerificationLink(): void {
    // Send verification link via email
    this.isSendingVerificationLink = true;
    this.customerService.sendVerificationLink(parseInt(this.customerId)).subscribe({
      next: (response) => {
        this.snackBarService.openSnackBar(response.message || 'Verification link sent successfully', 'success-class');
        this.isSendingVerificationLink = false;
      },
      error: (error) => {
        console.error('Error sending verification link:', error);
        this.snackBarService.openSnackBar(
          error.error?.message || 'Error sending verification link',
          'error-class'
        );
        this.isSendingVerificationLink = false;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/dashboard/customer-list']);
  }

  toggleSelectAll(): void {
    // Toggle the select all state
    this.selectAll = !this.selectAll;    
    // Apply the new state to all non-verified documents only
    this.documents.forEach(doc => {
      if (!doc.verified) {
        doc.selected = this.selectAll;
      }
    });
    }

  onDocumentSelect(document: DocumentItem, event: any): void {
    // Get the checked state from the change event
    const isChecked = event.checked;
    document.selected = isChecked;    
    // Update the select all state based on current selections
    this.updateSelectAllState();
  }

  updateSelectAllState(): void {
    const selectableDocuments = this.documents.filter(doc => !doc.verified);
    const selectedCount = selectableDocuments.filter(doc => doc.selected).length;
    const totalCount = selectableDocuments.length;
    
    // Update selectAll based on how many non-verified documents are selected
    this.selectAll = totalCount > 0 && selectedCount === totalCount;
  }

  approveSelected(): void {
    const selectedDocs = this.documents.filter(doc => doc.selected);
    if (selectedDocs.length === 0) {
      this.snackBarService.openSnackBar('Please select documents to approve', 'error-class');
      return;
    }

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      disableClose: true,
      data: {
        title: 'Confirm Approval',
        message: `Are you sure you want to approve ${selectedDocs.length} document${selectedDocs.length > 1 ? 's' : ''}?`,
        isLoading: () => this.isApprovingDocuments,
        onConfirm: () => this.verifyDocuments(selectedDocs, dialogRef)
      }
    });
  }

  verifyDocuments(selectedDocs: DocumentItem[], dialogRef?: any): void {
    // Prepare payload for bulk-verify-fields API
    this.isApprovingDocuments = true;
    const payload = selectedDocs.map(doc => ({
      field_key: doc.id,
      status: 'verified'
    }));

    const apiUrl = `${environment.BASE_URL}/bulk-verify-fields`;

    this.http.post<any>(apiUrl, { customer_id: this.customerId, fields: payload }).subscribe({
      next: (response) => {
        this.snackBarService.openSnackBar(`${selectedDocs.length} documents approved successfully`, 'success-class');
        
        // Add to activity log
        this.activityLog.unshift({
          id: Date.now().toString(),
          action: 'Documents Approved',
          timestamp: new Date(),
          user: 'Admin',
          details: `Approved ${selectedDocs.length} documents`
        });
        
        // Clear selections
        this.documents.forEach(doc => doc.selected = false);
        this.selectAll = false;
        
        // Refresh verification data
        this.loadVerificationData();
        this.isApprovingDocuments = false;
        this.loadActivityLog();

        
        // Close dialog after API completes
        if (dialogRef) {
          dialogRef.close(true);
        }
      },
      error: (error) => {
        console.error('Error verifying documents:', error);
        const errorMsg = error?.error?.message || 'Failed to approve documents. Please try again.';
        this.snackBarService.openSnackBar(errorMsg, 'error-class');
        this.isApprovingDocuments = false;
        
        // Close dialog after API completes (even on error)
        if (dialogRef) {
          dialogRef.close(false);
        }
      }
    });
  }

  updateSelected(): void {
    // Method removed as requested
  }

  getSelectedCount(): number {
    return this.documents.filter(doc => doc.selected).length;
  }

  hasSelectableDocuments(): boolean {
    return this.documents.some(doc => doc.selected && !doc.verified);
  }

  getActivityType(action: string): string {
    if (action.includes('Approved')) return 'success';
    if (action.includes('Uploaded')) return 'info';
    if (action.includes('Updated')) return 'warning';
    if (action.includes('Started')) return 'primary';
    return 'default';
  }

  generateFieldId(name: string): string {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '_').substring(0, 50);
  }

  generateFileId(): string {
    return Date.now().toString() + Math.random().toString(36).substring(2, 9);
  }

  openFileUrl(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  redirectToUploadSection(documentId: string): void {
    // Redirect to verification page with document ID to focus on upload section
    const token = this.route.snapshot.queryParamMap.get('token');
    if (token) {
      this.router.navigate(['/verification', { token: token }], {
        queryParams: { focusDocument: documentId }
      });
    }
  }

  // Getter methods for customer data
  get customerName(): string {
    return this.customerData?.customer_name || this.verificationData?.customer_name || '';
  }

  get customerEmail(): string {
    return this.customerData?.customer_email || this.customerData?.email || this.verificationData?.email || '';
  }

  get customerPhone(): string {
    return this.customerData?.customer_phone || this.customerData?.phone || this.verificationData?.customer_phone || '';
  }
}